// PageLayout: how a page uses the width it is given. Four kinds:
//   workspace  lists, tables, dashboards, boards
//   record     one thing (a project, an event, a PO): the main area, and beside it a side that
//              holds the property panels, with the activity thread beneath them
//   focus      a single form, a wizard, sign-in: capped and centred, on purpose
//   prose      long reading: capped by line length, not by the page
// workspace and record pages are capped and centred at a named width (standard unless said
// otherwise); `full` is a deliberate choice for what genuinely uses the width: boards,
// schedules, grids, maps. A wide table on a 4K monitor is not that: it pancakes.
// A record whose main area is a wide document can pin one rail panel beside it instead
// (pinnedRail): beside only from 1760px, sticky, scrolling itself. A record whose side is its
// conversation (an event, a tour) takes `thread`: the event page's own geometry, kept exactly.
// docs/operations/design-standard.md, "Layout".

import React from 'react';

const GUTTER = 'w-full px-4 pb-8 sm:px-6 2xl:px-8';

/** The named page widths. `full` has no cap. */
export type PageWidth = 'narrow' | 'standard' | 'wide' | 'full';

const CAP: Record<PageWidth, string> = {
  narrow: 'mx-auto max-w-[56rem]', // 896px: settings, simple pages
  standard: 'mx-auto max-w-[80rem]', // 1280px: most tables and records
  wide: 'mx-auto max-w-[100rem]', // 1600px: big tables, the Events list
  full: '',
};

// pinnedRail: from 1760px the pinned panel (22.5rem) and its gap sit BESIDE the capped main
// area, so the cap grows by that much there instead of squeezing the document.
const PINNED_CAP: Record<PageWidth, string> = {
  narrow: 'mx-auto max-w-[56rem] min-[1760px]:max-w-[79.5rem]',
  standard: 'mx-auto max-w-[80rem] min-[1760px]:max-w-[103.5rem]',
  wide: 'mx-auto max-w-[100rem] min-[1760px]:max-w-[123.5rem]',
  full: '',
};

export interface PageLayoutProps {
  kind: 'workspace' | 'record' | 'focus' | 'prose';
  /**
   * workspace and record: how wide the page may grow. `standard` (1280px) unless said; `full`
   * only for boards, schedules, grids and maps. focus and prose have caps of their own.
   */
  width?: PageWidth;
  /** Header, back link, title: spans the page's width above the columns. */
  header?: React.ReactNode;
  /**
   * record only: the property panels. Pass them as siblings (a fragment): each becomes one cell
   * in a 22rem column from `lg`. On a `full` page they pair into two columns from 1680px, where
   * a pair stretches to the taller of the two so the row ends on one line.
   */
  rail?: React.ReactNode;
  /** record only: the activity thread, under the property panels and as wide as they are. */
  aside?: React.ReactNode;
  /**
   * record only: the rail is one pinned panel beside a document that needs its width (the PO
   * page's billing beside a 13-column lines table). It sits beside the main area only from
   * 1760px, stacks under it below that, and when beside it stays in view and scrolls itself.
   */
  pinnedRail?: boolean;
  /**
   * record only: the message thread, in a 20rem column that stays in view beside the main area
   * from `lg`, after it below `lg` (MessageThread draws its own floating bubble there). The page
   * caps at 72rem. This is the event page's geometry, which the tour page shares; `width`, `rail`
   * and `aside` do not apply.
   */
  thread?: React.ReactNode;
  children: React.ReactNode;
  /**
   * The page's own rhythm (`pt-4 space-y-5`, `flex flex-col`). Lands on the element that holds
   * the header and children, inside the width cap, so it spaces the sections it is given. On a
   * page with a rail it sits on the outer edge; the main column brings its own spacing there.
   */
  className?: string;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  kind, width = 'standard', header, rail, aside, pinnedRail, thread, children, className = '',
}) => {
  if (kind === 'record' && thread) {
    return (
      <div className={`mx-auto max-w-6xl px-4 py-6 ${className}`}>
        {header}
        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="min-w-0 flex-1 space-y-5">{children}</div>
          <div className="self-start lg:sticky lg:top-4 lg:w-80 lg:flex-shrink-0">{thread}</div>
        </div>
      </div>
    );
  }
  if (kind === 'focus' || kind === 'prose') {
    const cap = kind === 'focus' ? 'max-w-2xl' : 'max-w-[65ch]';
    return (
      <div className={GUTTER}>
        <div className={`mx-auto ${cap} ${className}`}>
          {header}
          {children}
        </div>
      </div>
    );
  }

  if (kind === 'workspace' || (!rail && !aside)) {
    return (
      <div className={GUTTER}>
        <div className={`${CAP[width]} ${className}`}>
          {header}
          {children}
        </div>
      </div>
    );
  }

  if (pinnedRail) {
    // 1760px is where a 1400px table, the 22.5rem rail and the gap all fit; svh because vh
    // lies under mobile browser chrome.
    return (
      <div className={`${GUTTER} ${className}`}>
        <div className={rail ? PINNED_CAP[width] : CAP[width]}>
          {header}
          <div className="grid min-w-0 grid-cols-1 items-start gap-4 min-[1760px]:grid-cols-[minmax(0,1fr)_22.5rem]">
            <div className="min-w-0 space-y-4">{children}</div>
            <div className="grid min-w-0 grid-cols-1 gap-4 min-[1760px]:sticky min-[1760px]:top-4 min-[1760px]:max-h-[calc(100svh-2rem)] min-[1760px]:overflow-y-auto">
              {rail}
              {aside}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // The 1680px pairing reads the screen, not the page, so it only holds where the page is the
  // screen: a capped page keeps one 22rem rail column, or the pair would squeeze the main area.
  const paired = width === 'full';
  return (
    <div className={`${GUTTER} ${className}`}>
      <div className={CAP[width]}>
        {header}
        <div className={`grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] ${paired ? 'min-[1680px]:grid-cols-[minmax(0,1fr)_minmax(0,45rem)]' : ''}`}>
          <div className="min-w-0 space-y-4">{children}</div>
          <div className={`grid min-w-0 grid-cols-1 gap-4 ${paired ? 'min-[1680px]:grid-cols-2' : ''}`}>
            {rail}
            {aside && <div className={`min-w-0 ${paired ? 'min-[1680px]:col-span-2' : ''}`}>{aside}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
